import React, { useState } from 'react';
import { X, MessageSquare, Loader2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeedbackModal({ isOpen, onClose }) {
  const [message, setMessage] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message || message.trim().length < 10) {
      toast.error('Please enter a message (at least 10 characters)');
      return;
    }

    setSubmitting(true);

    try {
      // Submit feedback
      await axios.post(`${API}/feedback`, {
        message: message.trim(),
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null
      });

      toast.success('Thank you for your feedback!');
      
      // Reset form
      setMessage('');
      setContactName('');
      setContactEmail('');
      onClose();
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
      <div className="bg-[#121212] rounded-2xl border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Send Feedback</h2>
              <p className="text-sm text-neutral-400">Help us improve R3VIT</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Message */}
          <div className="space-y-2">
            <Label className="text-white">
              Your Feedback <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think, report a bug, or suggest a feature..."
              className="bg-[#0a0a0a] border-white/10 text-white min-h-[120px] resize-none"
              maxLength={2000}
            />
            <p className="text-xs text-neutral-500 text-right">
              {message.length}/2000
            </p>
          </div>

          {/* Contact Info */}
          <div className="space-y-4 pt-2 border-t border-white/10">
            <p className="text-sm text-neutral-400">
              Optional: Add contact info if you'd like us to follow up
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white text-sm">Name</Label>
                <Input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Your name"
                  className="bg-[#0a0a0a] border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white text-sm">Email</Label>
                <Input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[#0a0a0a] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting || message.trim().length < 10}
            className="w-full bg-red-500 hover:bg-red-600 h-12 rounded-full font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Send Feedback
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
